import React, { useState, useEffect, useRef } from "react";
import classNames from "classnames/bind";
import styles from "./AddPoint.module.css";
import images from "~/assets";
import { NavLink } from "react-router-dom";
import routes from "~/configs";
import { BASE_TRANSACTIONS_URL } from "~/helper/apiService";

const cx = classNames.bind(styles);

function AddPoint() {
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState({
    loading: false,
    error: null,
    success: false,
  });
  const benefitsGridRef = useRef(null);
  const sectionTitleRefs = useRef([]);

  const validatePhone = (phone) => /^(03|05|07|08|09)[0-9]{8,9}$/.test(phone);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validatePhone(phone)) {
      setStatus({
        loading: false,
        error: "Số điện thoại không hợp lệ!",
        success: false,
      });
      return;
    }

    setStatus({ loading: true, error: null, success: false });
    try {
      const res = await fetch(`${BASE_TRANSACTIONS_URL}add-point`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });

      if (!res.ok) throw new Error("Gửi thất bại!");

      setStatus({ loading: false, error: null, success: true });
      setPhone("");
    } catch (err) {
      setStatus({ loading: false, error: err.message, success: false });
    }
  };

  const handleScroll = (e, sectionId) => {
    e.preventDefault();
    const section = document.getElementById(sectionId);
    if (section) {
      const headerHeight = 78;
      const sectionPosition = section.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: sectionPosition - headerHeight,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (entry.target.classList.contains(cx("benefits-grid"))) {
              const cards = entry.target.querySelectorAll(`.${cx("benefit-card")}`);
              cards.forEach((card, index) => {
                setTimeout(() => {
                  card.classList.add(cx("visible"));
                }, index * 150);
              });
            } else {
              entry.target.classList.add(cx("visible"));
            }
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (benefitsGridRef.current) {
      observer.observe(benefitsGridRef.current);
    }

    sectionTitleRefs.current.forEach((title) => {
      if (title) observer.observe(title);
    });

    return () => {
      if (benefitsGridRef.current) {
        observer.unobserve(benefitsGridRef.current);
      }
      sectionTitleRefs.current.forEach((title) => {
        if (title) observer.unobserve(title);
      });
    };
  }, []);

  return (
    <div className={cx("site-wrapper")}>
      <link
        rel="stylesheet"
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.2/css/all.min.css"
      />
      <header className={cx("header")}>
        <h1 className={cx("logo")}>
          <img
            src={images.logo}
            alt="Xanh 19 Logo"
            className={cx("logo-imgs")}
          />
          Xanh19
        </h1>
        <nav className={cx("nav")}>
          <a href="#benefits" onClick={(e) => handleScroll(e, "benefits")}>
            Quyền lợi
          </a>
          <a href="#contact" onClick={(e) => handleScroll(e, "contact")}>
            Liên hệ
          </a>
          <NavLink to={routes.history}>Xem điểm</NavLink>
        </nav>
      </header>

      <div className={cx("container")}>
        <section
          className={cx("hero")}
          style={{ background: `url(${images.bgr}) center/cover no-repeat` }}
        >
          <div className={cx("hero-content")}>
            <section id="register" className={cx("section", "highlight")}>
              <h3
                className={cx("form-title")}
                ref={(el) => (sectionTitleRefs.current[0] = el)}
              >
                Đăng ký tích điểm
              </h3>
              <div className={cx("form-container")}>
                <form onSubmit={handleSubmit} className={cx("form")}>
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Nhập số điện thoại..."
                    className={cx("input", { error: status.error })}
                  />
                  <button
                    type="submit"
                    disabled={status.loading}
                    className={cx("cta-btn")}
                  >
                    {status.loading ? "Đang gửi..." : "Gửi"}
                  </button>
                  {status.error && (
                    <p className={cx("error")}>{status.error}</p>
                  )}
                  {status.success && (
                    <p className={cx("success")}>✅Cảm ơn Quý khách! Hệ thống đã cộng +1 điểm vào tài khoản. Chúc Quý khách chuyến đi vui vẻ!</p>
                  )}
                </form>
              </div>
            </section>
          </div>
        </section>

        <section id="benefits" className={cx("section", "benefits")}>
          <h3
            className={cx("section-title")}
            ref={(el) => (sectionTitleRefs.current[1] = el)}
          >
            Quyền lợi khi tham gia
          </h3>
          <div className={cx("benefits-grid")} ref={benefitsGridRef}>
            <div className={cx("benefit-card")}>
              <i className={cx("icon", "fa-solid", "fa-star")}></i>
              <div className={cx("benefit-content")}>
                <h4>Mỗi chuyến đi</h4>
                <p>
                  Nhận <strong>1 điểm</strong> cho mỗi chuyến đi hoàn thành.
                </p>
              </div>
            </div>
            <div className={cx("benefit-card")}>
              <i className={cx("icon", "fa-solid", "fa-gift")}></i>
              <div className={cx("benefit-content")}>
                <h4>5 điểm</h4>
                <p>
                  Giảm ngay <strong>50K</strong> cho chuyến đi tiếp theo.
                </p>
              </div>
            </div>
            <div className={cx("benefit-card")}>
              <i className={cx("icon", "fa-solid", "fa-car")}></i>
              <div className={cx("benefit-content")}>
                <h4>10 điểm</h4>
                <p>
                  Nhận <strong>1 chuyến đi miễn phí</strong> (tối đa 100K).
                </p>
              </div>
            </div>
            <div className={cx("benefit-card")}>
              <i className={cx("icon", "fa-solid", "fa-ticket-alt")}></i>
              <div className={cx("benefit-content")}>
                <h4>20 điểm</h4>
                <p>
                  Nhận <strong>2 chuyến miễn phí</strong> + Voucher{" "}
                  <strong>10%</strong>.
                </p>
              </div>
            </div>
          </div>
          <p className={cx("note")}>
            📌 Lưu ý: Nếu bạn sử dụng ưu đãi tại một mốc bất kỳ, điểm sẽ reset
            về 0 và tính lại từ đầu!
          </p>
        </section>

        <section id="contact" className={cx("section", "contact")}>
          <h3
            className={cx("section-title")}
            ref={(el) => (sectionTitleRefs.current[2] = el)}
          >
            Liên hệ
          </h3>
          <div className={cx("contact-content")}>
            <p className={cx("contact-note")}>
              Quý khách vui lòng liên hệ qua các kênh chính thức sau để đặt xe, đảm bảo quyền lợi và nhận hỗ trợ tốt nhất :
            </p>
            <div className={cx("contact-text")}>
              <p>
                1️⃣ Hotline: <a href="tel:0843348888" className={cx("contact-link")}>0843 348 888</a> - <a href="tel:0564170123" className={cx("contact-link")}>0564 170 123</a>
              </p>
              <p>2️⃣ Fanpage: Xanh19 Group - Việt Trì - Hà Nội</p>
              <p>
                🔗 <a href="https://www.facebook.com/share/19aBsEnTai/" className={cx("contact-link")}>https://www.facebook.com/share/19aBsEnTai/</a>
              </p>
              <p>3️⃣ Zalo: <a href="tel:0843348888" className={cx("contact-link")}>0843 348 888</a></p>
              <p>
                🔗 <a href="http://zaloapp.com/qr/p/140gr9a6v9qqw" className={cx("contact-link")}>http://zaloapp.com/qr/p/140gr9a6v9qqw</a>
              </p>
            </div>
            <img className={cx("foot-img")} src={images.footImg} alt="" />
          </div>
        </section>
      </div>
    </div>
  );
}

export default AddPoint;